import { type FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';
import type { LineListSB } from '@mwl/core-lib';
import { Button } from '@/components/Button/Button';
import { GroupedLines } from '@/components/GroupedLines/GroupedLines';
import { sportLinesEnrichment } from '@/utils';
import { getAnalytics } from './SportSearchLines.analytics';
import type { SportSearchLinesProps } from './SportSearchLines.types';
import styles from './SportSearchLines.module.scss';
const defaultVisibleCount = 5;
const maxVisibleCount = 20;
const SportSearchLines: FC<SportSearchLinesProps> = ({
  className,
  searchResultData,
  events
}) => {
  const {
    t
  } = useTranslation('sport', {
    keyPrefix: 'search'
  });
  const [visibleCount, setVisibleCount] = useState(defaultVisibleCount);
  const {
    searchResult,
    ...rest
  } = searchResultData;
  const lines = useMemo(() => {
    return sportLinesEnrichment((({
      lines: searchResult?.lines,
      ...rest
    } as unknown) as LineListSB));
  }, [searchResult, rest]);
  const activeEvent = useMemo(() => events?.find(event => event.selected), [events]);
  useEffect(() => {
    setVisibleCount(defaultVisibleCount);
  }, [searchResultData]);
  if (!lines.length) {
    return null;
  }
  const isShowMore = visibleCount < Math.min(maxVisibleCount, lines.length);
  const handleShowMore = () => {
    setVisibleCount(maxVisibleCount);
  };
  return <div className={cn(styles.root, className)} data-sentry-component="SportSearchLines" data-sentry-source-file="SportSearchLines.tsx">
      <div className={styles.header}>
        <h2 className={styles.title}>{t('events', 'Events')}</h2>
      </div>

      <div className={styles.lines}>
        <GroupedLines lines={lines.slice(0, visibleCount)} isInSearch variant={activeEvent?.type} data-sentry-element="GroupedLines" data-sentry-source-file="SportSearchLines.tsx" />
      </div>

      {isShowMore && <Button className={styles.loadMoreButton} as="button" color="red" variant="filled" size="lg" rounded uppercase fontSize="xl" analytics={getAnalytics(t('show_more_lines', 'Show more'))} onClick={handleShowMore}>
          {t('show_more_lines', 'Show more')}
        </Button>}
    </div>;
};
export { SportSearchLines };